import { Box, Button } from "@mui/material";
import { useFormik } from "formik";
import { ChangeEvent, useEffect, useState } from "react";
import * as Yup from "yup";
import { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import Flex from "@/components/Flex";
import StyledDialog from "@/components/StyledDialog";
import Text from "@/components/Text";
import SignatureInput from "@/components/signiture/SignatureInput";
import { useSignatureController } from "@/components/signiture/use-signature-controller";
import { VisuallyHiddenInput } from "@/components/VisuallyHiddenInput";
import { useAddSignature } from "@/queries/signature/use-add-signature";
import {
  convertToBase64,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
interface SignatureModalProps {
  disclosureHost: DisclosureHost;
  title: string;
  providerUuid: string;
}

type SignFormik = {
  providerSignature?: string;
};

const initialValues = {
  providerSignature: "",
};

const SIGNATURE_UPLOAD_ACCEPT = ".jpg,.jpeg,.png";
const SIGNATURE_UPLOAD_MIME_TYPES = ["image/jpeg", "image/png"];
const SIGNATURE_UPLOAD_ERROR = "Only .jpg, .jpeg and .png files are allowed.";

const validationSchema = Yup.object().shape({
  providerSignature: Yup.string().optional().label("Signature"),
});

const SignatureModal = ({
  disclosureHost,
  title,
  providerUuid,
}: SignatureModalProps) => {
  const [_k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    disclosureHost.opened,
    disclosureHost.data?.providerSignature
  );
  const showSnackbar = useSnackbar();
  const client = useQueryClient();
  const { mutateAsync: addSignature } = useAddSignature();

  const uploadSignatureHandler = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.item(0);
    event.target.value = "";
    if (!file || !signPad || !canvasRef.current) return;

    if (!SIGNATURE_UPLOAD_MIME_TYPES.includes(file.type)) {
      showSnackbar({ message: SIGNATURE_UPLOAD_ERROR, type: "error" });
      return;
    }

    try {
      const dataUrl = `data:${file.type};base64,${await convertToBase64(file)}`;
      const image = new Image();
      image.src = dataUrl;
      await image.decode();

      // Fit inside the pad keeping aspect ratio, so Save stores it like a drawn signature.
      const { width, height } = canvasRef.current;
      const scale = Math.min(width / image.width, height / image.height);
      signPad.clear();
      await signPad.fromDataURL(dataUrl, {
        ratio: 1,
        width: image.width * scale,
        height: image.height * scale,
        xOffset: (width - image.width * scale) / 2,
        yOffset: (height - image.height * scale) / 2,
      });
      setK((prev) => !prev);
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  };

  const signOffHandler = () => {
    // if (isSignEmpty()) {
    //   showSnackbar({
    //     message: "You must sign before submitting.",
    //     type: "error",
    //   });
    //   return;
    // }

    // 'provider'?: { [key: string]: string; };

    // 'providerSignature'?: string;

    const payload = {
      provider: { [providerUuid]: "" },
      providerSignature: signPad?.toDataURL(),
      // providerSignature: v?.providerSignature,
      // providerSignatureImage: signPad?.toDataURL(),
    };

    const transformedPayload = {
      providerSignature: payload,
      xTENANTID: getXtenantId(),
    };
    // console.log(payload);
    showToastAndResetForm(addSignature(transformedPayload as any));
  };

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        formik.resetForm();
        client.invalidateQueries({
          queryKey: ["provider-signature-by-providerID"],
        });
        showSnackbar(getSuccessSnackbar(res));
        disclosureHost.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  useEffect(() => {
    if (disclosureHost.opened) {
      formik.setFieldValue("providerSignature", "");
      // Check if there's existing signature data to display
      if (disclosureHost.data?.providerSignature) {
        // Wait for signPad to be ready before loading the signature
        if (signPad) {
          signPad.fromDataURL(disclosureHost.data.providerSignature);
          formik.setFieldValue(
            "providerSignature",
            disclosureHost.data.providerSignature
          );
        }
      }
    }
  }, [disclosureHost.opened, disclosureHost.data?.providerSignature]);

  useEffect(() => {
    // When signPad becomes available, check if we need to load existing signature
    if (
      signPad &&
      disclosureHost.opened &&
      disclosureHost.data?.providerSignature
    ) {
      signPad.fromDataURL(disclosureHost.data.providerSignature);
      formik.setFieldValue(
        "providerSignature",
        disclosureHost.data.providerSignature
      );
    }
  }, [signPad, disclosureHost.opened, disclosureHost.data?.providerSignature]);

  const formik = useFormik<SignFormik>({
    initialValues: disclosureHost.opened ? disclosureHost.data : initialValues,
    validationSchema,
    onSubmit: signOffHandler,
  });

  return (
    <StyledDialog
      onClose={disclosureHost.close}
      open={disclosureHost.opened}
      dialogTitle={title}
      dialogAction={
        <Flex justifyContent={"flex-end"} gap={2}>
          <Button
            onClick={disclosureHost.close}
            variant="outlined"
            style={{ width: "80px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={formik.submitForm}
            variant="contained"
            style={{ width: "70px" }}
          >
            Save
          </Button>
        </Flex>
      }
    >
      <Flex flexDirection={"column"} width={"600px"}>
        <Flex width={"100%"} gap={2}>
          <Box position={"relative"}>
            <SignatureInput
              error={formik.submitCount > 0 && isSignEmpty()}
              label="Signature"
              canvasRef={canvasRef}
              w="600px"
              h="330px"
            />
            <Flex position={"absolute"} bottom={10} right={10} gap={1}>
              <Button
                component="label"
                variant="outlined"
                sx={{ height: "24px", bgcolor: "white" }}
              >
                Upload Signature
                <VisuallyHiddenInput
                  type="file"
                  accept={SIGNATURE_UPLOAD_ACCEPT}
                  onChange={uploadSignatureHandler}
                />
              </Button>
              <Button
                variant="contained"
                sx={{ height: "24px" }}
                onClick={() => {
                  setK((prev) => !prev);
                  signPad?.clear();
                }}
              >
                clear
              </Button>
            </Flex>
          </Box>
        </Flex>
        <Flex width={"100%"} justifyContent={"center"} mt={1}>
          <Text>
            Draw your signature using your mouse or touch screen, or upload an
            image (.jpg, .jpeg, .png)
          </Text>
        </Flex>
      </Flex>
    </StyledDialog>
  );
};

export default SignatureModal;
